import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Toolbar,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslation } from "react-i18next";

const drawerWidth = 260;
const navItems = ["expertise", "history", "projects", "contact"];

function Navigation({ parentToChild, modeChange }: any) {
  const { mode } = parentToChild;
  const isDark = mode === "dark";
  const { t, i18n } = useTranslation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [language, setLanguage] = useState<"pt" | "en" | "es">("pt");

  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  const handleLanguageChange = (event: any) => {
    const lang = event.target.value as "pt" | "en" | "es";
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const scrollToSection = (section: string) => {
    const el = document.getElementById(section);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    if (mobileOpen) setMobileOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LanguageSelect = (
    <Select
      value={language}
      onChange={handleLanguageChange}
      size="small"
      MenuProps={{
        disableScrollLock: true,
        PaperProps: {
          sx: {
            bgcolor: isDark ? "#1e1e1e" : "#fff",
            color: isDark ? "#fff" : "#0d1116",
            borderRadius: 1,
            boxShadow: "0 6px 18px rgba(0,0,0,.25)",
          },
        },
      }}
      sx={{
        color: isDark ? "#fff" : "#0d1116",
        border: `1px solid ${
          isDark ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.2)"
        }`,
        "& .MuiSelect-icon": { color: isDark ? "#fff" : "#0d1116" },
        minWidth: 100,
      }}
      renderValue={(value) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
          <img
            src={
              value === "pt"
                ? "https://flagcdn.com/w20/br.png"
                : value === "en"
                ? "https://flagcdn.com/w20/us.png"
                : value === "es"
                ? "https://flagcdn.com/w20/es.png"
                : "https://flagcdn.com/w20/cn.png"
            }
            width={20}
            height={14}
            alt="flag"
            style={{ borderRadius: 2 }}
          />
          {value.toUpperCase()}
        </Box>
      )}
    >
      <MenuItem value="pt">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src="https://flagcdn.com/w20/br.png" width={20} height={14} alt="BR" />
          Português
        </Box>
      </MenuItem>
      <MenuItem value="en">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src="https://flagcdn.com/w20/us.png" width={20} height={14} alt="US" />
          English
        </Box>
      </MenuItem>
      <MenuItem value="es">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src="https://flagcdn.com/w20/es.png" width={20} height={14} alt="ES" />
          Español
        </Box>
      </MenuItem>
      <MenuItem value="zh">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src="https://flagcdn.com/w20/cn.png" width={20} height={14} alt="CN" />
          简体中文
        </Box>
      </MenuItem>
    </Select>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* 🔳 Navbar transparente com blur ao rolar */}
      <AppBar
        component="nav"
        elevation={0}
        sx={{
          position: "fixed",
          width: "100%",
          zIndex: 1300,
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.25)" : "none",
          backgroundColor: scrolled
            ? isDark
              ? "rgba(13, 17, 22, 0.9)"
              : "rgba(248, 249, 250, 0.9)"
            : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          transition: "background-color 0.4s ease, backdrop-filter 0.4s ease",
          color: isDark ? "#fff" : "#0d1116",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: 64, px: { xs: 2, sm: 4 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Menu desktop */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, ml: "auto" }}>
            {navItems.map((id) => (
              <Button
                key={id}
                onClick={() => scrollToSection(id)}
                sx={{
                  color: "inherit",
                  fontWeight: 500,
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { color: isDark ? "#9ecbff" : "#5000ca" },
                }}
              >
                {t(id)}
              </Button>
            ))}
            {LanguageSelect}
            <IconButton onClick={() => modeChange()} sx={{ color: "inherit" }}>
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true, disableScrollLock: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            backgroundColor: isDark ? "#0d1116" : "#f8f9fa",
            color: isDark ? "#fff" : "#0d1116",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            pt: "64px", // 🟢 evita sobreposição com o AppBar
            boxSizing: "border-box",
          },
        }}
      >
        {/* Itens principais */}
        <Box>
          {navItems.map((id) => (
            <ListItemButton
              key={id}
              onClick={() => scrollToSection(id)}
              sx={{
                borderRadius: 1,
                "&:hover": {
                  background: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
                },
              }}
            >
              <ListItemText
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  textAlign: "left",
                }}
                primary={t(id)}
              />
            </ListItemButton>
          ))}
        </Box>

        {/* Seção inferior fixa */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${isDark ? "#333" : "#ddd"}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          {LanguageSelect}
          <IconButton
            onClick={() => modeChange()}
            sx={{
              color: isDark ? "#fff" : "#0d1116",
              border: `1px solid ${isDark ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.25)"}`,
              borderRadius: 1,
            }}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Box>
      </Drawer>

    </Box>
  );
}

export default Navigation;